import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Banner, BlockStack, Button, Card, Checkbox, DataTable, DropZone, FormLayout, InlineStack, Link, Modal, Page, Select, Text, TextField } from '@shopify/polaris';
import { deleteRequest, getRequest, postRequest, putRequest } from '../../../../shared/components/functions';
import Toasts from '../../../../shared/components/toasts/toasts.jsx';
import { useLookupStore } from '../../../../context/useStore';
import ReactSelect from 'react-select';
import AsyncSelect from 'react-select/async';
import { DeleteIcon, PlusIcon } from '@shopify/polaris-icons';
import { getPublicAssetUrl } from '../../../../utils/publicAssetUrl';

const DEFAULT_BU_FORM = {
  Code: '',
  Name: '',
  SortOrder: '0',
  IsActive: true,
  EmailSignatureBannerUrl: '',
  EmailSignatureBannerWidth: '480',
  EmailSignatureBannerHeight: '120',
  EmailSignatureBannerAlt: '',
  EmailSignatureBannerUpdatedAt: '',
  EmailSignatureBrandColor: '#383838',
  EmailSignatureWebsite: '',
  EmailSignaturePhone: '',
  EmailSignatureLinks: []
};

function parseSignatureLinks(value) {
  let raw = value;
  if (!Array.isArray(raw)) {
    try {
      raw = JSON.parse(String(value || '[]'));
    } catch (err) {
      raw = [];
    }
  }

  return (Array.isArray(raw) ? raw : []).map((link) => ({ label: String(link?.label || '').trim(), url: String(link?.url || '').trim() }));
}

const DEFAULT_RULE_FORM = {
  BusinessUnitID: '',
  DocumentDirection: 'ANY',
  DocumentType: 'ANY',
  RuleType: 'SUPPLIER_ID',
  RuleValue: '',
  MatchMode: 'EXACT',
  Priority: '100',
  Confidence: '0.85',
  IsActive: true
};

const DEFAULT_RECLASSIFY_FORM = {
  dateFrom: '',
  dateTo: '',
  DocumentDirection: 'ANY',
  DocumentType: 'INVOICE',
  includeManualOverrides: false
};
const RECLASSIFY_POLL_INTERVAL_MS = 2000;
const RECLASSIFY_POLL_MAX_ATTEMPTS = 450;

const DOCUMENT_DIRECTION_OPTIONS = [
  { label: 'Any (incoming + outgoing)', value: 'ANY' },
  { label: 'Incoming only', value: 'IN' },
  { label: 'Outgoing only', value: 'OUT' }
];

const DOCUMENT_TYPE_OPTIONS = [
  { label: 'Any (invoice + creditnote)', value: 'ANY' },
  { label: 'Invoice only', value: 'INVOICE' },
  { label: 'Creditnote only', value: 'CREDITNOTE' }
];

const RULE_TYPE_OPTIONS = [
  { label: 'Supplier ID (FIRNR)', value: 'SUPPLIER_ID' },
  { label: 'Owner User ID (GebruikerID)', value: 'OWNER_ID' },
  { label: 'Cost Type ID (BestellingTypeID)', value: 'COST_TYPE_ID' },
  { label: 'Project ID', value: 'PROJECT_ID' },
  { label: 'Keyword (memo / reference / lines)', value: 'KEYWORD' }
];

const MATCH_MODE_OPTIONS = [
  { label: 'Exact match', value: 'EXACT' },
  { label: 'Contains text', value: 'CONTAINS' }
];

const SEARCH_SELECT_STYLES = {
  valueContainer: (base) => ({ ...base, minHeight: '32px', paddingTop: '0', paddingBottom: '0' }),
  indicatorsContainer: (base) => ({ ...base, minHeight: '32px' }),
  control: (base) => ({
    ...base,
    minHeight: '32px',
    borderRadius: '8px',
    borderColor: '#919191'
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 })
};

function normalizeBool(value) {
  return value === true || value === 1 || value === '1';
}

async function parseJsonResponse(response, { invalidSuccessMessage, invalidErrorMessage } = {}) {
  const rawBody = await response.text();
  if (!rawBody) return {};

  try {
    return JSON.parse(rawBody);
  } catch (parseErr) {
    throw new Error(response.ok ? invalidSuccessMessage || 'Invalid response format' : invalidErrorMessage || `Request failed (${response.status})`);
  }
}

function buildUserRuleLabel(user, fallbackId = '') {
  const userId = String(user?.GebruikerID ?? fallbackId ?? '').trim();
  const abbreviation = String(user?.Afkorting || '').trim();
  const username = String(user?.Gebruikernaam || user?.Gebruiker || '').trim();
  const userText = [abbreviation, username].filter(Boolean).join(' - ');
  const inactiveLabel = normalizeBool(user?.Inactief) ? ' [inactive]' : '';

  if (userText && userId) return `${userText} (#${userId})${inactiveLabel}`;
  if (userText) return `${userText}${inactiveLabel}`;
  if (userId) return `User #${userId}`;
  return 'User';
}

function buildCompanyRuleLabel(company, fallbackId = '') {
  const supplierId = String(company?.FIRNR ?? fallbackId ?? '').trim();
  const name = String(company?.NAAM || '').trim();
  if (name && supplierId) return `${name} (#${supplierId})`;
  if (name) return name;
  if (supplierId) return `Company #${supplierId}`;
  return 'Company';
}

export default function SettingsBusinessUnitsPage() {
  const setBusinessunits = useLookupStore((state) => state.setBusinessunits);
  const users = useLookupStore((state) => state.users);
  const setUsers = useLookupStore((state) => state.setUsers);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasRequestedUsers, setHasRequestedUsers] = useState(false);
  const [companyLabelsById, setCompanyLabelsById] = useState({});

  const [buModalOpen, setBuModalOpen] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingBuId, setEditingBuId] = useState(null);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [buForm, setBuForm] = useState(DEFAULT_BU_FORM);
  const [ruleForm, setRuleForm] = useState(DEFAULT_RULE_FORM);
  const [savingBu, setSavingBu] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [runningReclassification, setRunningReclassification] = useState(false);
  const [reclassifyForm, setReclassifyForm] = useState(DEFAULT_RECLASSIFY_FORM);
  const [reclassificationJob, setReclassificationJob] = useState(null);
  const [reclassificationSummary, setReclassificationSummary] = useState(null);
  const [toast, setToast] = useState(false);
  const [errorToast, setErrorToast] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [fetchedBusinessUnits, fetchedRules] = await Promise.all([getRequest('/api/business_units'), getRequest('/api/business_units/rules/all')]);
      setBusinessUnits(Array.isArray(fetchedBusinessUnits) ? fetchedBusinessUnits : []);
      setRules(Array.isArray(fetchedRules) ? fetchedRules : []);
    } catch (err) {
      console.error('Error loading business units/rules:', err);
      setErrorToast(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (hasRequestedUsers || (Array.isArray(users) && users.length > 0)) return;
    setHasRequestedUsers(true);
    setUsers();
  }, [hasRequestedUsers, users, setUsers]);

  const businessUnitOptions = useMemo(
    () =>
      businessUnits
        .slice()
        .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0) || String(a.Name || '').localeCompare(String(b.Name || '')))
        .map((unit) => ({
          label: `${unit.Code} (${unit.Name})`,
          value: String(unit.BusinessUnitID)
        })),
    [businessUnits]
  );

  const ownerRuleOptions = useMemo(
    () =>
      (Array.isArray(users) ? users : [])
        .filter((user) => user?.GebruikerID != null)
        .slice()
        .sort((a, b) => String(a.Gebruikernaam || a.Gebruiker || a.Afkorting || '').localeCompare(String(b.Gebruikernaam || b.Gebruiker || b.Afkorting || '')))
        .map((user) => ({
          value: String(user.GebruikerID),
          label: buildUserRuleLabel(user)
        })),
    [users]
  );

  const ownerRuleLabelById = useMemo(
    () =>
      ownerRuleOptions.reduce((acc, option) => {
        acc[String(option.value)] = option.label;
        return acc;
      }, {}),
    [ownerRuleOptions]
  );

  const loadSupplierOptions = useCallback(async (inputValue) => {
    const searchText = String(inputValue || '').trim();
    if (!searchText) return [];

    try {
      const data = await getRequest(`/api/companies/simple/search?q=NAAM:${searchText}`);
      const options = Array.isArray(data)
        ? data
            .map((company) => {
              const supplierId = String(company?.FIRNR || '').trim();
              const addressParts = [company?.STRAAT_NR, company?.POSTNR, company?.WOONPL].filter(Boolean);
              const addressLabel = addressParts.length ? ` (${addressParts.join(', ')})` : '';
              const label = `${buildCompanyRuleLabel(company, supplierId)}${addressLabel}`;
              return { value: supplierId, label };
            })
            .filter((option) => option.value)
        : [];

      if (options.length) {
        setCompanyLabelsById((prev) => {
          const next = { ...prev };
          options.forEach((option) => {
            if (option?.value) next[String(option.value)] = option.label;
          });
          return next;
        });
      }
      return options;
    } catch (err) {
      console.error('Error loading supplier options:', err);
      return [];
    }
  }, []);

  useEffect(() => {
    const supplierIds = Array.from(
      new Set(
        rules
          .filter((rule) => String(rule?.RuleType || '').toUpperCase() === 'SUPPLIER_ID')
          .map((rule) => String(rule?.RuleValue || '').trim())
          .filter(Boolean)
      )
    );
    const missingSupplierIds = supplierIds.filter((supplierId) => !companyLabelsById[supplierId]);
    if (!missingSupplierIds.length) return;

    let isCancelled = false;
    Promise.all(
      missingSupplierIds.map(async (supplierId) => {
        try {
          const company = await getRequest(`/api/companies/${supplierId}`);
          return { supplierId, label: buildCompanyRuleLabel(company, supplierId) };
        } catch (err) {
          return { supplierId, label: `Company #${supplierId}` };
        }
      })
    ).then((results) => {
      if (isCancelled) return;
      setCompanyLabelsById((prev) => {
        const next = { ...prev };
        results.forEach(({ supplierId, label }) => {
          if (supplierId) next[String(supplierId)] = label;
        });
        return next;
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [rules, companyLabelsById]);

  const openAddBusinessUnitModal = () => {
    setEditingBuId(null);
    setBuForm(DEFAULT_BU_FORM);
    setBuModalOpen(true);
  };

  const openEditBusinessUnitModal = (unit) => {
    setEditingBuId(unit.BusinessUnitID);
    setBuForm({
      Code: String(unit.Code || ''),
      Name: String(unit.Name || ''),
      SortOrder: String(unit.SortOrder ?? 0),
      IsActive: normalizeBool(unit.IsActive),
      EmailSignatureBannerUrl: String(unit.EmailSignatureBannerUrl || ''),
      EmailSignatureBannerWidth: String(unit.EmailSignatureBannerWidth || 480),
      EmailSignatureBannerHeight: String(unit.EmailSignatureBannerHeight || 120),
      EmailSignatureBannerAlt: String(unit.EmailSignatureBannerAlt || ''),
      EmailSignatureBannerUpdatedAt: String(unit.EmailSignatureBannerUpdatedAt || ''),
      EmailSignatureBrandColor: String(unit.EmailSignatureBrandColor || '#383838'),
      EmailSignatureWebsite: String(unit.EmailSignatureWebsite || ''),
      EmailSignaturePhone: String(unit.EmailSignaturePhone || ''),
      EmailSignatureLinks: parseSignatureLinks(unit.EmailSignatureLinks)
    });
    setBuModalOpen(true);
  };

  const openAddRuleModal = () => {
    setEditingRuleId(null);
    setRuleForm({
      ...DEFAULT_RULE_FORM,
      BusinessUnitID: businessUnitOptions[0]?.value || ''
    });
    setRuleModalOpen(true);
  };

  const openEditRuleModal = (rule) => {
    setEditingRuleId(rule.BusinessUnitRuleID);
    setRuleForm({
      BusinessUnitID: String(rule.BusinessUnitID || ''),
      DocumentDirection: String(rule.DocumentDirection || 'ANY').toUpperCase(),
      DocumentType: String(rule.DocumentType || 'ANY').toUpperCase(),
      RuleType: String(rule.RuleType || 'SUPPLIER_ID').toUpperCase(),
      RuleValue: String(rule.RuleValue || ''),
      MatchMode: String(rule.MatchMode || 'EXACT').toUpperCase(),
      Priority: String(rule.Priority ?? 100),
      Confidence: String(rule.Confidence ?? 0.85),
      IsActive: normalizeBool(rule.IsActive)
    });
    setRuleModalOpen(true);
  };

  const saveBusinessUnit = async () => {
    const code = String(buForm.Code || '')
      .trim()
      .toUpperCase();
    const name = String(buForm.Name || '').trim();
    const sortOrder = Number(buForm.SortOrder || 0);
    const bannerWidth = Number(buForm.EmailSignatureBannerWidth || 480);
    const bannerHeight = Number(buForm.EmailSignatureBannerHeight || 120);
    const brandColor = String(buForm.EmailSignatureBrandColor || '#383838').trim();

    if (!code || !name || Number.isNaN(sortOrder) || Number.isNaN(bannerWidth) || Number.isNaN(bannerHeight)) {
      setErrorToast(true);
      return;
    }

    setSavingBu(true);
    const payload = {
      Code: code,
      Name: name,
      SortOrder: sortOrder,
      IsActive: !!buForm.IsActive,
      EmailSignatureBannerUrl: String(buForm.EmailSignatureBannerUrl || '').trim(),
      EmailSignatureBannerWidth: bannerWidth,
      EmailSignatureBannerHeight: bannerHeight,
      EmailSignatureBannerAlt: String(buForm.EmailSignatureBannerAlt || '').trim() || null,
      EmailSignatureBrandColor: brandColor,
      EmailSignatureWebsite: String(buForm.EmailSignatureWebsite || '').trim(),
      EmailSignaturePhone: String(buForm.EmailSignaturePhone || '').trim() || null,
      EmailSignatureLinks: JSON.stringify(parseSignatureLinks(buForm.EmailSignatureLinks).filter((link) => link.label && link.url))
    };

    try {
      if (editingBuId) {
        await putRequest(`/api/business_units/${editingBuId}`, payload, setToast, setErrorToast);
      } else {
        await postRequest('/api/business_units', payload, setToast, setErrorToast);
      }

      await fetchData();
      await setBusinessunits();
      setBuModalOpen(false);
    } catch (err) {
      console.error('Error saving business unit:', err);
      setErrorToast(true);
    } finally {
      setSavingBu(false);
    }
  };

  const readImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Could not read image dimensions'));
      };
      image.src = objectUrl;
    });

  const uploadBusinessUnitBanner = async (files) => {
    const file = files?.[0];
    if (!editingBuId || !file) return;

    let displayWidth = Number(buForm.EmailSignatureBannerWidth) || 480;
    let displayHeight = Number(buForm.EmailSignatureBannerHeight) || 120;
    try {
      // Derive display height from the image's real aspect ratio so the signature never stretches the banner
      const natural = await readImageDimensions(file);
      displayWidth = Math.min(displayWidth, natural.width);
      displayHeight = Math.round(displayWidth * (natural.height / natural.width));
    } catch (err) {
      console.error('Error reading banner dimensions, keeping configured size:', err);
    }

    const formData = new FormData();
    formData.append('banner', file);
    formData.append('width', String(displayWidth));
    formData.append('height', String(displayHeight));

    setUploadingBanner(true);
    try {
      const response = await fetch(`/api/business_units/${editingBuId}/email-signature-banner`, {
        method: 'POST',
        body: formData
      });
      const result = await parseJsonResponse(response, {
        invalidSuccessMessage: 'Invalid banner upload response format',
        invalidErrorMessage: `Banner upload failed (${response.status})`
      });

      if (!response.ok) throw new Error(result?.msg || `Banner upload failed (${response.status})`);

      setBuForm((prev) => ({
        ...prev,
        EmailSignatureBannerUrl: String(result?.EmailSignatureBannerUrl || ''),
        EmailSignatureBannerWidth: String(result?.EmailSignatureBannerWidth || prev.EmailSignatureBannerWidth || 480),
        EmailSignatureBannerHeight: String(result?.EmailSignatureBannerHeight || prev.EmailSignatureBannerHeight || 120),
        EmailSignatureBannerUpdatedAt: String(result?.EmailSignatureBannerUpdatedAt || '')
      }));
      setToast(true);
      await fetchData();
      await setBusinessunits();
    } catch (err) {
      console.error('Error uploading business unit banner:', err);
      setErrorToast(true);
    } finally {
      setUploadingBanner(false);
    }
  };

  const saveRule = async () => {
    const businessUnitId = Number(ruleForm.BusinessUnitID);
    const documentDirection = String(ruleForm.DocumentDirection || 'ANY').toUpperCase();
    const documentType = String(ruleForm.DocumentType || 'ANY').toUpperCase();
    const ruleType = String(ruleForm.RuleType || '').toUpperCase();
    const ruleValue = String(ruleForm.RuleValue || '').trim();
    const matchMode = String(ruleForm.MatchMode || 'EXACT').toUpperCase();
    const priority = Number(ruleForm.Priority || 100);
    const confidence = Number(ruleForm.Confidence || 0.85);
    const validDirection = DOCUMENT_DIRECTION_OPTIONS.some((option) => option.value === documentDirection);
    const validType = DOCUMENT_TYPE_OPTIONS.some((option) => option.value === documentType);

    if (!businessUnitId || !ruleType || !ruleValue || Number.isNaN(priority) || Number.isNaN(confidence) || !validDirection || !validType) {
      setErrorToast(true);
      return;
    }

    setSavingRule(true);
    const payload = {
      BusinessUnitID: businessUnitId,
      DocumentDirection: documentDirection,
      DocumentType: documentType,
      RuleType: ruleType,
      RuleValue: ruleValue,
      MatchMode: matchMode,
      Priority: priority,
      Confidence: confidence,
      IsActive: !!ruleForm.IsActive
    };

    try {
      if (editingRuleId) {
        await putRequest(`/api/business_units/rules/${editingRuleId}`, payload, setToast, setErrorToast);
      } else {
        await postRequest('/api/business_units/rules', payload, setToast, setErrorToast);
      }

      await fetchData();
      setRuleModalOpen(false);
    } catch (err) {
      console.error('Error saving business unit rule:', err);
      setErrorToast(true);
    } finally {
      setSavingRule(false);
    }
  };

  const deleteRule = async (rule) => {
    if (!rule?.BusinessUnitRuleID) return;
    if (!confirm(`Delete rule #${rule.BusinessUnitRuleID}?`)) return;

    try {
      const result = await deleteRequest(`/api/business_units/rules/${rule.BusinessUnitRuleID}`);
      if (!result) {
        setErrorToast(true);
        return;
      }
      setToast(true);
      await fetchData();
    } catch (err) {
      console.error('Error deleting business unit rule:', err);
      setErrorToast(true);
    }
  };

  const runReclassification = async () => {
    const dateFrom = String(reclassifyForm.dateFrom || '').trim();
    const dateTo = String(reclassifyForm.dateTo || '').trim();

    if (!dateFrom || !dateTo || dateFrom > dateTo) {
      setErrorToast(true);
      return;
    }

    setRunningReclassification(true);
    setReclassificationJob(null);
    setReclassificationSummary(null);
    try {
      const pollJobUntilDone = async (jobId) => {
        for (let attempt = 0; attempt < RECLASSIFY_POLL_MAX_ATTEMPTS; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, RECLASSIFY_POLL_INTERVAL_MS));
          const statusResponse = await fetch(`/api/business_units/reclassify/${jobId}`);
          const statusResult = await parseJsonResponse(statusResponse, {
            invalidSuccessMessage: 'Invalid reclassification status format',
            invalidErrorMessage: `Failed to fetch reclassification status (${statusResponse.status})`
          });

          if (!statusResponse.ok) {
            throw new Error(statusResult?.msg || `Failed to fetch reclassification status (${statusResponse.status})`);
          }

          setReclassificationJob(statusResult);
          const status = String(statusResult?.status || '').toUpperCase();
          if (status === 'COMPLETED') {
            return statusResult?.summary || null;
          }
          if (status === 'FAILED') {
            throw new Error(statusResult?.error || statusResult?.msg || 'Reclassification job failed');
          }
        }
        throw new Error('Reclassification is still running. Please check again in a moment.');
      };

      const response = await fetch('/api/business_units/reclassify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...reclassifyForm, runAsync: true })
      });

      const result = await parseJsonResponse(response, {
        invalidSuccessMessage: 'Invalid reclassification response format',
        invalidErrorMessage: `Reclassification failed (${response.status})`
      });

      const shouldPollExistingJob = (response.status === 202 || response.status === 409) && result?.jobId;
      if (shouldPollExistingJob) {
        setReclassificationJob(result);
        const summary = await pollJobUntilDone(result.jobId);
        if (summary) {
          setReclassificationSummary(summary);
        }
        setToast(true);
        await fetchData();
        await setBusinessunits();
        return;
      }

      if (!response.ok) {
        throw new Error(result?.msg || `Failed to run reclassification (${response.status})`);
      }

      setReclassificationSummary(result);
      setToast(true);
      await fetchData();
      await setBusinessunits();
    } catch (err) {
      console.error('Error running reclassification:', err);
      setErrorToast(true);
    } finally {
      setRunningReclassification(false);
    }
  };

  const businessUnitRows = businessUnits
    .slice()
    .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0) || String(a.Name || '').localeCompare(String(b.Name || '')))
    .map((unit) => [
      unit.Code,
      unit.Name,
      unit.EmailSignaturePhone || (
        <Text as="span" tone="subdued">
          Default
        </Text>
      ),
      unit.EmailSignatureBannerUrl ? (
        <Link removeUnderline url={getPublicAssetUrl(unit.EmailSignatureBannerUrl)} external>
          Banner
        </Link>
      ) : (
        <Badge tone="warning">No banner</Badge>
      ),
      <Badge tone={normalizeBool(unit.IsActive) ? 'success' : 'critical'}>{normalizeBool(unit.IsActive) ? 'Active' : 'Inactive'}</Badge>,
      String(unit.SortOrder ?? 0),
      <Link removeUnderline onClick={() => openEditBusinessUnitModal(unit)}>
        Edit
      </Link>
    ]);

  const formatRuleValue = useCallback(
    (rule) => {
      const ruleType = String(rule?.RuleType || '').toUpperCase();
      const rawValue = String(rule?.RuleValue || '').trim();
      if (!rawValue) return '-';

      if (ruleType === 'OWNER_ID') {
        return ownerRuleLabelById[rawValue] || `User #${rawValue}`;
      }
      if (ruleType === 'SUPPLIER_ID') {
        return companyLabelsById[rawValue] || `Company #${rawValue}`;
      }
      return rawValue;
    },
    [ownerRuleLabelById, companyLabelsById]
  );

  const rulesRows = rules
    .slice()
    .sort((a, b) => (a.Priority || 0) - (b.Priority || 0))
    .map((rule) => [
      String(rule.Priority ?? ''),
      rule.BusinessUnitCode || '-',
      DOCUMENT_DIRECTION_OPTIONS.find((option) => option.value === String(rule.DocumentDirection || 'ANY').toUpperCase())?.label || 'Any (incoming + outgoing)',
      DOCUMENT_TYPE_OPTIONS.find((option) => option.value === String(rule.DocumentType || 'ANY').toUpperCase())?.label || 'Any (invoice + creditnote)',
      rule.RuleType || '-',
      formatRuleValue(rule),
      rule.MatchMode || '-',
      String(rule.Confidence ?? ''),
      <Badge tone={normalizeBool(rule.IsActive) ? 'success' : 'critical'}>{normalizeBool(rule.IsActive) ? 'Active' : 'Inactive'}</Badge>,
      <Link removeUnderline onClick={() => openEditRuleModal(rule)}>
        Edit
      </Link>,
      <Link removeUnderline monochrome onClick={() => deleteRule(rule)}>
        Delete
      </Link>
    ]);

  const selectedOwnerOption = useMemo(() => {
    const ownerId = String(ruleForm.RuleValue || '').trim();
    if (!ownerId) return null;
    return ownerRuleOptions.find((option) => String(option.value) === ownerId) || { value: ownerId, label: ownerRuleLabelById[ownerId] || `User #${ownerId}` };
  }, [ruleForm.RuleValue, ownerRuleOptions, ownerRuleLabelById]);

  const selectedSupplierOption = useMemo(() => {
    const supplierId = String(ruleForm.RuleValue || '').trim();
    if (!supplierId) return null;
    return { value: supplierId, label: companyLabelsById[supplierId] || `Company #${supplierId}` };
  }, [ruleForm.RuleValue, companyLabelsById]);

  const ruleTypeHelpText = useMemo(() => {
    switch (ruleForm.RuleType) {
      case 'SUPPLIER_ID':
        return 'Search supplier by company name. The rule stores the supplier FIRNR.';
      case 'OWNER_ID':
        return 'Search owner by user name or abbreviation. The rule stores the GebruikerID.';
      case 'COST_TYPE_ID':
        return 'Use BestellingTypeID, for example: 1';
      case 'PROJECT_ID':
        return 'Use project id';
      case 'KEYWORD':
        return 'Use text to match in invoice memo/reference/line text';
      default:
        return '';
    }
  }, [ruleForm.RuleType]);

  const reclassificationRows = useMemo(
    () =>
      Array.isArray(reclassificationSummary?.targets)
        ? reclassificationSummary.targets.map((target) => [target.label, String(target.matched ?? 0), String(target.updated ?? 0), `${Number(target.durationMs || 0) / 1000}s`])
        : [],
    [reclassificationSummary]
  );

  const reclassificationJobStatusText = useMemo(() => {
    const status = String(reclassificationJob?.status || '').toUpperCase();
    if (!status) return '';
    if (status === 'RUNNING') return 'Running';
    if (status === 'QUEUED') return 'Queued';
    if (status === 'FAILED') return 'Failed';
    if (status === 'COMPLETED') return 'Completed';
    return status;
  }, [reclassificationJob]);

  return (
    <Page
      title="Business units & assignment rules"
      fullWidth
      backAction={{ content: 'Settings', url: '/settings' }}
      primaryAction={{ content: 'Add business unit', onAction: openAddBusinessUnitModal }}
      secondaryActions={[{ content: 'Add assignment rule', onAction: openAddRuleModal, disabled: businessUnitOptions.length === 0 }]}
    >
      <Toasts toast={toast} setToast={setToast} errorToast={errorToast} setErrorToast={setErrorToast} />

      <BlockStack gap="400">
        <Card>
          <BlockStack gap="300">
            <Banner tone="info">
              <Text as="p">
                Rules are evaluated by ascending priority (lowest number first). Each rule can be scoped by document direction (incoming/outgoing) and document type (invoice/creditnote).
              </Text>
            </Banner>
            {loading ? (
              <Text as="p">Loading business units...</Text>
            ) : (
              <DataTable
                increasedTableDensity
                stickyHeader
                verticalAlign="middle"
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text']}
                headings={[<b>Code</b>, <b>Name</b>, <b>Phone</b>, <b>Email banner</b>, <b>Status</b>, <b>Sort order</b>, <b>Edit</b>]}
                rows={businessUnitRows}
                footerContent={businessUnitRows.length === 0 ? 'No business units found' : undefined}
              />
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between">
              <Text as="h2" variant="headingMd">
                Assignment rules
              </Text>
              <Button onClick={openAddRuleModal} disabled={businessUnitOptions.length === 0}>
                Add rule
              </Button>
            </InlineStack>
            {loading ? (
              <Text as="p">Loading rules...</Text>
            ) : (
              <DataTable
                increasedTableDensity
                stickyHeader
                verticalAlign="middle"
                columnContentTypes={['numeric', 'text', 'text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'text', 'text']}
                headings={[<b>Priority</b>, <b>BU</b>, <b>Direction</b>, <b>Document</b>, <b>Rule type</b>, <b>Value</b>, <b>Match</b>, <b>Confidence</b>, <b>Status</b>, <b>Edit</b>, <b>Delete</b>]}
                rows={rulesRows}
                footerContent={rulesRows.length === 0 ? 'No rules found' : undefined}
              />
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between">
              <Text as="h2" variant="headingMd">
                Bulk recategorization
              </Text>
              <Button variant="primary" onClick={runReclassification} loading={runningReclassification}>
                Reclassify
              </Button>
            </InlineStack>
            <Text as="p" tone="subdued">
              Re-runs BU classification for documents in the date range using the current active rules. Manual overrides are excluded by default.
            </Text>
            <FormLayout>
              <TextField label="Date from" type="date" autoComplete="off" value={reclassifyForm.dateFrom} onChange={(value) => setReclassifyForm((prev) => ({ ...prev, dateFrom: value }))} />
              <TextField label="Date to" type="date" autoComplete="off" value={reclassifyForm.dateTo} onChange={(value) => setReclassifyForm((prev) => ({ ...prev, dateTo: value }))} />
              <Select
                label="Document direction"
                options={DOCUMENT_DIRECTION_OPTIONS}
                value={reclassifyForm.DocumentDirection}
                onChange={(value) => setReclassifyForm((prev) => ({ ...prev, DocumentDirection: value }))}
              />
              <Select label="Document type" options={DOCUMENT_TYPE_OPTIONS} value={reclassifyForm.DocumentType} onChange={(value) => setReclassifyForm((prev) => ({ ...prev, DocumentType: value }))} />
              <Checkbox
                label="Include documents manually assigned to a BU"
                checked={!!reclassifyForm.includeManualOverrides}
                onChange={(value) => setReclassifyForm((prev) => ({ ...prev, includeManualOverrides: value }))}
              />
            </FormLayout>

            {runningReclassification && reclassificationJob ? (
              <Banner tone="info">
                <Text as="p">Reclassification job {reclassificationJobStatusText ? `(${reclassificationJobStatusText})` : ''}...</Text>
              </Banner>
            ) : null}

            {reclassificationSummary ? (
              <BlockStack gap="200">
                <Banner tone="success">
                  <Text as="p">
                    Reclassification done: {reclassificationSummary.totalUpdated} documents updated ({reclassificationSummary.totalMatched} matched).
                  </Text>
                </Banner>
                {reclassificationRows.length > 0 ? (
                  <DataTable
                    increasedTableDensity
                    stickyHeader
                    verticalAlign="middle"
                    columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                    headings={[<b>Target</b>, <b>Matched</b>, <b>Updated</b>, <b>Duration</b>]}
                    rows={reclassificationRows}
                  />
                ) : null}
              </BlockStack>
            ) : null}
          </BlockStack>
        </Card>
      </BlockStack>

      <Modal
        open={buModalOpen}
        onClose={() => setBuModalOpen(false)}
        title={editingBuId ? `Edit business unit #${editingBuId}` : 'Add business unit'}
        primaryAction={{ content: editingBuId ? 'Save changes' : 'Add business unit', onAction: saveBusinessUnit, loading: savingBu }}
      >
        <Modal.Section>
          <FormLayout>
            <TextField label="Code" autoComplete="off" value={buForm.Code} onChange={(value) => setBuForm((prev) => ({ ...prev, Code: value }))} requiredIndicator />
            <TextField label="Name" autoComplete="off" value={buForm.Name} onChange={(value) => setBuForm((prev) => ({ ...prev, Name: value }))} requiredIndicator />
            <TextField label="Sort order" type="number" autoComplete="off" value={String(buForm.SortOrder)} onChange={(value) => setBuForm((prev) => ({ ...prev, SortOrder: value }))} />
            <TextField
              label="Website"
              autoComplete="off"
              value={buForm.EmailSignatureWebsite}
              onChange={(value) => setBuForm((prev) => ({ ...prev, EmailSignatureWebsite: value }))}
              placeholder="www.schuilenburg.com"
              helpText="Shown as the website link in email signatures for this business unit. Users can still override it per signature."
            />
            <TextField
              label="Phone"
              type="tel"
              autoComplete="off"
              value={buForm.EmailSignaturePhone}
              onChange={(value) => setBuForm((prev) => ({ ...prev, EmailSignaturePhone: value }))}
              placeholder="+32 52 49 90 70"
              maxLength={50}
              helpText="Company phone in email signatures for this business unit. Leave empty to use the main number (+32 52 49 90 70). A user's own mobile number still replaces it."
            />
            <BlockStack gap="200">
              <Text as="p" variant="headingSm">
                Extra signature links
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                Shown after the website in email signatures, e.g. LinkedIn or Instagram. The wording is what users' contacts will see; the URL is where it points to.
              </Text>
              {buForm.EmailSignatureLinks.map((link, index) => (
                <InlineStack key={index} gap="200" blockAlign="start" wrap={false}>
                  <div style={{ flex: '1 1 0' }}>
                    <TextField
                      label="Wording"
                      labelHidden={index > 0}
                      autoComplete="off"
                      placeholder="LinkedIn"
                      value={link.label}
                      onChange={(value) => setBuForm((prev) => ({ ...prev, EmailSignatureLinks: prev.EmailSignatureLinks.map((row, i) => (i === index ? { ...row, label: value } : row)) }))}
                    />
                  </div>
                  <div style={{ flex: '2 1 0' }}>
                    <TextField
                      label="Link"
                      labelHidden={index > 0}
                      autoComplete="off"
                      placeholder="www.linkedin.com/company/..."
                      value={link.url}
                      onChange={(value) => setBuForm((prev) => ({ ...prev, EmailSignatureLinks: prev.EmailSignatureLinks.map((row, i) => (i === index ? { ...row, url: value } : row)) }))}
                      connectedRight={
                        <Button
                          icon={DeleteIcon}
                          accessibilityLabel="Remove link"
                          onClick={() => setBuForm((prev) => ({ ...prev, EmailSignatureLinks: prev.EmailSignatureLinks.filter((row, i) => i !== index) }))}
                        />
                      }
                    />
                  </div>
                </InlineStack>
              ))}
              <div>
                <Button icon={PlusIcon} onClick={() => setBuForm((prev) => ({ ...prev, EmailSignatureLinks: [...prev.EmailSignatureLinks, { label: '', url: '' }] }))}>
                  Add link
                </Button>
              </div>
            </BlockStack>
            <TextField
              label="Email signature banner URL"
              autoComplete="off"
              value={buForm.EmailSignatureBannerUrl}
              onChange={(value) => setBuForm((prev) => ({ ...prev, EmailSignatureBannerUrl: value }))}
              helpText="Use an uploaded public URL or paste an existing HTTPS image URL."
            />
            <FormLayout.Group>
              <TextField
                label="Banner display width"
                type="number"
                autoComplete="off"
                value={String(buForm.EmailSignatureBannerWidth)}
                onChange={(value) => setBuForm((prev) => ({ ...prev, EmailSignatureBannerWidth: value }))}
                helpText="Max width in the signature. On upload, the height is recalculated from the image so it never distorts."
              />
              <TextField
                label="Banner display height"
                type="number"
                autoComplete="off"
                value={String(buForm.EmailSignatureBannerHeight)}
                onChange={(value) => setBuForm((prev) => ({ ...prev, EmailSignatureBannerHeight: value }))}
                helpText="Set automatically on upload to match the image's aspect ratio."
              />
            </FormLayout.Group>
            <TextField
              label="Banner alt text"
              autoComplete="off"
              value={buForm.EmailSignatureBannerAlt}
              onChange={(value) => setBuForm((prev) => ({ ...prev, EmailSignatureBannerAlt: value }))}
              helpText="Shown instead of the banner when a mail app blocks pictures, e.g. the banner's tagline. Defaults to the business unit name."
            />
            <div>
              <div style={{ marginBottom: '4px' }}>
                <Text as="p">Email signature brand color</Text>
              </div>
              <InlineStack gap="200" blockAlign="center">
                <input
                  aria-label="Email signature brand color"
                  type="color"
                  value={buForm.EmailSignatureBrandColor}
                  onChange={(event) => setBuForm((prev) => ({ ...prev, EmailSignatureBrandColor: event.target.value }))}
                  style={{ width: 48, height: 36, border: '1px solid #c9cccf', borderRadius: 6, padding: 2, background: '#fff' }}
                />
                <Text as="span" tone="subdued">
                  {buForm.EmailSignatureBrandColor}
                </Text>
              </InlineStack>
            </div>
            {editingBuId ? (
              <BlockStack gap="200">
                <Text as="p" variant="headingSm">
                  Upload email banner
                </Text>
                {buForm.EmailSignatureBannerUrl ? (
                  <img
                    src={`${getPublicAssetUrl(buForm.EmailSignatureBannerUrl)}${buForm.EmailSignatureBannerUpdatedAt ? `?v=${encodeURIComponent(buForm.EmailSignatureBannerUpdatedAt)}` : ''}`}
                    alt="Email signature banner preview"
                    style={{ display: 'block', maxWidth: '100%', width: 320, height: 'auto', border: '1px solid #dfe3e8', borderRadius: 6 }}
                  />
                ) : null}
                <DropZone accept="image/png,image/jpeg,image/webp" type="image" allowMultiple={false} onDrop={uploadBusinessUnitBanner} disabled={uploadingBanner}>
                  <DropZone.FileUpload
                    actionTitle={uploadingBanner ? 'Uploading banner...' : 'Upload banner image'}
                    actionHint="PNG, JPG or WebP, stored as an email-safe PNG or JPG. Upload at double size for retina sharpness. Re-uploading keeps the same public link, so linked signatures update automatically and embedded ones get a reminder."
                  />
                </DropZone>
              </BlockStack>
            ) : (
              <Banner tone="info">Save the business unit first, then edit it to upload a public email banner.</Banner>
            )}
            <Checkbox label="Active" checked={!!buForm.IsActive} onChange={(value) => setBuForm((prev) => ({ ...prev, IsActive: value }))} />
          </FormLayout>
        </Modal.Section>
      </Modal>

      <Modal
        open={ruleModalOpen}
        onClose={() => setRuleModalOpen(false)}
        title={editingRuleId ? `Edit rule #${editingRuleId}` : 'Add assignment rule'}
        primaryAction={{ content: editingRuleId ? 'Save changes' : 'Add rule', onAction: saveRule, loading: savingRule }}
      >
        <Modal.Section>
          <FormLayout>
            <Select label="Business unit" options={businessUnitOptions} value={ruleForm.BusinessUnitID} onChange={(value) => setRuleForm((prev) => ({ ...prev, BusinessUnitID: value }))} />
            <Select
              label="Document direction"
              options={DOCUMENT_DIRECTION_OPTIONS}
              value={ruleForm.DocumentDirection}
              onChange={(value) => setRuleForm((prev) => ({ ...prev, DocumentDirection: value }))}
            />
            <Select label="Document type" options={DOCUMENT_TYPE_OPTIONS} value={ruleForm.DocumentType} onChange={(value) => setRuleForm((prev) => ({ ...prev, DocumentType: value }))} />
            <Select
              label="Rule type"
              options={RULE_TYPE_OPTIONS}
              value={ruleForm.RuleType}
              onChange={(value) =>
                setRuleForm((prev) => ({
                  ...prev,
                  RuleType: value,
                  RuleValue: prev.RuleType === value ? prev.RuleValue : '',
                  MatchMode: value === 'KEYWORD' ? prev.MatchMode : 'EXACT'
                }))
              }
            />
            <Text variant="bodySm" as="p" tone="subdued">
              {ruleTypeHelpText}
            </Text>
            {ruleForm.RuleType === 'SUPPLIER_ID' ? (
              <div>
                <div style={{ marginBottom: '4px' }}>
                  <Text as="p">Supplier</Text>
                </div>
                <AsyncSelect
                  placeholder="Search company name..."
                  defaultOptions
                  cacheOptions
                  loadOptions={loadSupplierOptions}
                  value={selectedSupplierOption}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={SEARCH_SELECT_STYLES}
                  onChange={(option) => {
                    const supplierId = option?.value ? String(option.value) : '';
                    const supplierLabel = option?.label ? String(option.label) : '';
                    if (supplierId && supplierLabel) {
                      setCompanyLabelsById((prev) => ({ ...prev, [supplierId]: supplierLabel }));
                    }
                    setRuleForm((prev) => ({ ...prev, RuleValue: supplierId }));
                  }}
                />
              </div>
            ) : ruleForm.RuleType === 'OWNER_ID' ? (
              <div>
                <div style={{ marginBottom: '4px' }}>
                  <Text as="p">Owner user</Text>
                </div>
                <ReactSelect
                  placeholder="Search user name or abbreviation..."
                  options={ownerRuleOptions}
                  value={selectedOwnerOption}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={SEARCH_SELECT_STYLES}
                  onChange={(option) => setRuleForm((prev) => ({ ...prev, RuleValue: option?.value ? String(option.value) : '' }))}
                />
              </div>
            ) : (
              <TextField label="Rule value" autoComplete="off" value={ruleForm.RuleValue} onChange={(value) => setRuleForm((prev) => ({ ...prev, RuleValue: value }))} />
            )}
            <Select label="Match mode" options={MATCH_MODE_OPTIONS} value={ruleForm.MatchMode} onChange={(value) => setRuleForm((prev) => ({ ...prev, MatchMode: value }))} />
            <TextField
              label="Priority (lower = first)"
              type="number"
              autoComplete="off"
              value={String(ruleForm.Priority)}
              onChange={(value) => setRuleForm((prev) => ({ ...prev, Priority: value }))}
            />
            <TextField
              label="Confidence (0 to 1)"
              type="number"
              step={0.01}
              autoComplete="off"
              value={String(ruleForm.Confidence)}
              onChange={(value) => setRuleForm((prev) => ({ ...prev, Confidence: value }))}
            />
            <Checkbox label="Active" checked={!!ruleForm.IsActive} onChange={(value) => setRuleForm((prev) => ({ ...prev, IsActive: value }))} />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
